import { FormBuilder, FormGroup } from '@angular/forms';
import { computed, inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';

/**
 * interfaces
 */
import { IUnitTypeDetailsDto, IUnitTypeFormControls } from '../../interfaces';

/**
 * enums
 */
import { IOperationModeUnion, OperationModifyMode } from '@core/enums/operation-mode';

/**
 * validators
 */
import { AppValidators } from '@forms/validators';

/**
 * Manages the reactive form state for the unit type add/edit modal.
 * The record ID is NOT part of the form — it travels as a URL path parameter.
 */
@Injectable()
export class UnitTypeFormService {

    private readonly _fb = inject(FormBuilder);

    /**
     * init
     */
    private _form!: FormGroup<IUnitTypeFormControls>;

    /** The reactive form instance. */
    public get form(): FormGroup<IUnitTypeFormControls> {
        return this._form;
    }

    /**
     * init operationMode
     */
    private readonly _operationMode: WritableSignal<IOperationModeUnion | null> = signal(null);

    /** Read-only operation mode signal. */
    public readonly operationMode: Signal<IOperationModeUnion | null> = this._operationMode.asReadonly();

    /** True when the form is in create mode. */
    public readonly isCreationMode: Signal<boolean> = computed(() => {
        return this.operationMode() === OperationModifyMode.Create;
    });

    /**
     * init isLoading
     */
    private readonly _isLoading: WritableSignal<boolean> = signal(false);

    /** True while an API call triggered by the form is in flight. */
    public readonly isLoading: Signal<boolean> = this._isLoading.asReadonly();

    /**
     * init selectedId
     */
    private readonly _selectedId: WritableSignal<string | null> = signal(null);

    /** The ID of the record currently being edited; null in create mode. */
    public readonly selectedId: Signal<string | null> = this._selectedId.asReadonly();

    constructor() {
        this._form = this._initForm();
    }

    /**
     * build form group
     */
    private _initForm(): FormGroup<IUnitTypeFormControls> {
        return this._fb.group<IUnitTypeFormControls>({
            name:                       this._fb.control<string | null>(null, [AppValidators.required, AppValidators.minLength(2), AppValidators.maxLength(64)]),
            description:                this._fb.control<string | null>(null, [AppValidators.required, AppValidators.minLength(2), AppValidators.maxLength(256)]),
            default_level:              this._fb.control<number | null>(null, AppValidators.required),
            default_type_for_its_level: this._fb.control<boolean | null>(false),
            icon_id:                    this._fb.control<string | null>(null),
        });
    }

    /** Sets the operation mode (create / update). */
    public setOperationMode(mode: IOperationModeUnion): void {
        this._operationMode.set(mode);
    }

    /** Sets the loading state. */
    public setLoading(value: boolean): void {
        this._isLoading.set(value);
    }

    /** Sets the ID of the record being edited. */
    public setSelectedId(id: string | null): void {
        this._selectedId.set(id);
    }

    /** Patches the form with data from a unit type DTO (edit mode). */
    public patchValue(dto: IUnitTypeDetailsDto): void {
        this._form.patchValue({
            name:                       dto.name,
            description:                dto.description,
            default_level:              dto.default_level,
            default_type_for_its_level: dto.default_type_for_its_level,
            icon_id:                    null,
        });
    }

    /** Resets the form to its initial empty state. */
    public resetForm(): void {
        this._form.reset({ default_type_for_its_level: false });
    }

}
